import { ChevronDown, Grid2X2, LogOut, Menu, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/OidcProvider';
import { moduleRegistry } from '../modules/registry';
import { activeModuleId, isModuleAvailable, isModuleNavigationItemAvailable } from '../modules/types';
import { ThemeSwitch } from '../theme/ThemeSwitch';
import { useTheme } from '../theme/theme';

export function AppShell() {
  const { session, logout } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [navigationOpen, setNavigationOpen] = useState(false);
  const [compactNavigation, setCompactNavigation] = useState(() => window.matchMedia('(max-width: 1040px)').matches);

  // One group is open at a time, and by default it is the one the current page belongs to. The
  // sidebar carries every fachsystem with all of its sub-pages, which is a long list to scan for
  // the handful of entries that are reachable from where you already are.
  //
  // Derived from the registry rather than from the session-filtered list below, because hooks run
  // before the session check and the two agree on every module a signed-in user can navigate to.
  const currentModuleId = activeModuleId(moduleRegistry, pathname);
  const [expandedModuleId, setExpandedModuleId] = useState(currentModuleId);

  // Snap back to the current module whenever navigation leaves the one you were in. Moving
  // between sub-pages of the same module leaves currentModuleId untouched, so a group you opened
  // by hand to look somewhere else survives until you actually go somewhere else.
  //
  // Adjusted during render rather than in an effect. React re-runs this component before touching
  // the DOM, so the sidebar never paints the previous module's group open and then corrects
  // itself; an effect would do exactly that, and would also fight the lint rule that exists to
  // stop it.
  const [settledModuleId, setSettledModuleId] = useState(currentModuleId);
  if (settledModuleId !== currentModuleId) {
    setSettledModuleId(currentModuleId);
    setExpandedModuleId(currentModuleId);
  }
  const sidebarRef = useRef<HTMLElement>(null);
  const mobileBarRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(() => { window.scrollTo({ top: 0, left: 0 }); }, [pathname]);
  useEffect(() => {
    const query = window.matchMedia('(max-width: 1040px)');
    const update = (event: MediaQueryListEvent) => {
      setCompactNavigation(event.matches);
      if (!event.matches) setNavigationOpen(false);
    };
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  useEffect(() => {
    if (!navigationOpen || !compactNavigation) return undefined;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const sidebar = sidebarRef.current;
    const mobileBar = mobileBarRef.current;
    const content = contentRef.current;
    const previousOverflow = document.body.style.overflow;
    const focusable = () => [...(sidebar?.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])') ?? [])];
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { setNavigationOpen(false); return; }
      if (event.key !== 'Tab') return;
      const elements = focusable();
      if (elements.length === 0) { event.preventDefault(); return; }
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.body.style.overflow = 'hidden';
    content?.setAttribute('inert', '');
    mobileBar?.setAttribute('inert', '');
    window.addEventListener('keydown', handleKey);
    window.requestAnimationFrame(() => focusable()[0]?.focus());
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = previousOverflow;
      content?.removeAttribute('inert');
      mobileBar?.removeAttribute('inert');
      previousFocus?.focus();
    };
  }, [compactNavigation, navigationOpen]);
  if (!session) return null;
  const modules = moduleRegistry.filter((module) => isModuleAvailable(module, session));
  return <div className={`app-shell ${navigationOpen ? 'navigation-open' : ''}`}>
    <aside ref={sidebarRef} id="portal-mobile-navigation" className="service-sidebar" aria-label="Portalnavigation" role={navigationOpen && compactNavigation ? 'dialog' : undefined} aria-modal={navigationOpen && compactNavigation ? true : undefined}>
      <button className="sidebar-wordmark" onClick={() => { setNavigationOpen(false); navigate('/'); }} aria-label="Zum GES Portal"><img src="/brand/ges-logo-white.png" alt="" /><span>Portal</span></button>
      <nav className="service-navigation" aria-label="Fachsysteme und Bereiche">
        <NavLink className="overview-link" to="/" end onClick={() => setNavigationOpen(false)}><Grid2X2 size={18} /><span>Übersicht</span></NavLink>
        <div className="service-navigation-modules">{modules.map((module) => {
          const Icon = module.icon;
          const items = module.navigation.filter((item) => isModuleNavigationItemAvailable(item, session));
          const expanded = expandedModuleId === module.id;
          const childrenId = `navigation-${module.id}-children`;
          return <section className="service-navigation-group" key={module.id} aria-labelledby={`navigation-${module.id}`}>
            <div className="service-navigation-group-header">
              <NavLink className="service-navigation-heading" to={module.route} id={`navigation-${module.id}`} onClick={() => setNavigationOpen(false)}><Icon size={19} /><span>{module.title}</span></NavLink>
              {items.length > 0 && <button
                type="button"
                className="service-navigation-disclosure"
                aria-expanded={expanded}
                aria-controls={childrenId}
                aria-label={`${module.title}: Unterpunkte ${expanded ? 'einklappen' : 'ausklappen'}`}
                onClick={() => setExpandedModuleId(expanded ? null : module.id)}
              ><ChevronDown size={16} aria-hidden /></button>}
            </div>
            {/* The heading stays a link and the chevron is its own button: heading clicks are how
                you enter a module, and collapsing the one you are in - or looking into another
                without leaving this page - is a different intent that needs its own control. */}
            <div className="service-navigation-children" id={childrenId} hidden={!expanded}>{items.map((item) => {
              const ItemIcon = item.icon;
              return <NavLink key={item.route} to={item.route} end={item.end} aria-label={`${module.title}: ${item.title}`} onClick={() => setNavigationOpen(false)}><ItemIcon size={15} /><span>{item.title}</span></NavLink>;
            })}</div>
          </section>;
        })}</div>
      </nav>
      <ThemeSwitch />
      <footer className="sidebar-session">
        <div className="sidebar-identity"><span>{session.displayName}</span><small>{session.demo ? 'Demo · ' : ''}{session.roles[0] || 'Benutzer'}</small></div>
        <button className="sidebar-logout" onClick={() => void logout()} aria-label="Abmelden" title="Abmelden"><LogOut size={18} /></button>
      </footer>
    </aside>
    <header ref={mobileBarRef} className="mobile-shellbar">
      <button className="mobile-menu-button" onClick={() => setNavigationOpen((current) => !current)} aria-expanded={navigationOpen} aria-controls="portal-mobile-navigation" aria-label={navigationOpen ? 'Navigation schließen' : 'Navigation öffnen'}>{navigationOpen ? <X size={22} /> : <Menu size={22} />}</button>
      <button className="mobile-wordmark" onClick={() => { setNavigationOpen(false); navigate('/'); }} aria-label="Zum GES Portal"><img src={theme === 'dark' ? '/brand/ges-logo-white.png' : '/brand/ges-logo.png'} alt="" /></button>
      <button className="icon-button" onClick={() => void logout()} aria-label="Abmelden" title="Abmelden"><LogOut size={18} /></button>
    </header>
    <button className="navigation-scrim" aria-label="Navigation schließen" onClick={() => setNavigationOpen(false)} hidden={!navigationOpen} />
    <div ref={contentRef} className="shell-content"><Outlet /></div>
  </div>;
}
